/**
 * World Magnetic Model 2025 (NOAA NCEI / BGS), degree-12 spherical-harmonic main field with linear
 * secular variation. Implements the algorithm of the WMM2025 technical report (§1.2): geodetic →
 * geocentric-spherical conversion, Schmidt semi-normalised associated Legendre functions, field
 * components in the spherical frame, rotation back to the geodetic (ellipsoidal) frame.
 *
 * Aviation data spec §5: runway / wind °M = °T − declination (east positive). Declination is
 * reference data (class R), valid 2025.0–2030.0; outside that window the model is extrapolated and
 * `magneticField().outsideValidity` is true.
 */
import type { EpochMs } from './types.ts';
import { normalizeDeg } from './geo.ts';
import { WMM2025_COF } from './wmmcof.ts';

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

/** WGS84 semi-major axis (km), flattening, and the geomagnetic reference radius (km). */
const A_KM = 6378.137;
const F = 1 / 298.257223563;
const E2 = F * (2 - F);
const RE_KM = 6371.2;

export const WMM_EPOCH = 2025.0;
export const WMM_VALID_UNTIL = 2030.0;
export const WMM_MODEL_NAME = 'WMM-2025';

interface Coefficients {
  epoch: number;
  nMax: number;
  /** [n][m] */
  g: number[][];
  h: number[][];
  gd: number[][];
  hd: number[][];
}

function square(n: number): number[][] {
  return Array.from({ length: n + 1 }, () => new Array<number>(n + 1).fill(0));
}

/** Parse a NOAA .COF file (header line, then `n m g h gdot hdot`, terminated by 9999…). */
export function parseCof(text: string): Coefficients {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const header = lines.shift();
  if (!header) throw new Error('empty COF');
  const epoch = Number(header.split(/\s+/)[0]);
  if (!Number.isFinite(epoch)) throw new Error(`bad COF header: ${header}`);
  const rows: number[][] = [];
  let nMax = 0;
  for (const l of lines) {
    if (/^9{10,}/.test(l)) break;
    const f = l.split(/\s+/).map(Number);
    if (f.length < 6 || f.some((x) => !Number.isFinite(x))) throw new Error(`bad COF row: ${l}`);
    rows.push(f);
    nMax = Math.max(nMax, f[0]!);
  }
  const g = square(nMax);
  const h = square(nMax);
  const gd = square(nMax);
  const hd = square(nMax);
  for (const [n, m, gv, hv, gdv, hdv] of rows as Array<[number, number, number, number, number, number]>) {
    g[n]![m] = gv;
    h[n]![m] = hv;
    gd[n]![m] = gdv;
    hd[n]![m] = hdv;
  }
  return { epoch, nMax, g, h, gd, hd };
}

let wmm2025: Coefficients | null = null;
const model = () => (wmm2025 ??= parseCof(WMM2025_COF));

/** Schmidt quasi-normalisation factors S[n][m]. */
function schmidt(nMax: number): number[][] {
  const s = square(nMax);
  s[0]![0] = 1;
  for (let n = 1; n <= nMax; n++) {
    s[n]![0] = (s[n - 1]![0]! * (2 * n - 1)) / n;
    for (let m = 1; m <= n; m++) {
      s[n]![m] = s[n]![m - 1]! * Math.sqrt(((n - m + 1) * (m === 1 ? 2 : 1)) / (n + m));
    }
  }
  return s;
}
let schmidtCache: number[][] | null = null;

export interface MagneticField {
  /** North, east, down components (nT) in the geodetic frame. */
  x: number;
  y: number;
  z: number;
  /** Horizontal intensity, total intensity (nT). */
  h: number;
  f: number;
  /** Inclination (dip), degrees, positive down. */
  inclinationDeg: number;
  /** Declination, degrees, positive EAST of true north. */
  declinationDeg: number;
  /** Grid variation (polar regions only, |lat| > 55°), degrees; null elsewhere. */
  gridVariationDeg: number | null;
  decimalYear: number;
  outsideValidity: boolean;
}

/**
 * Full WMM2025 field at a geodetic position.
 * @param latDeg geodetic latitude, @param lonDeg longitude, @param altKm height above the WGS84 ellipsoid (km),
 * @param decimalYear e.g. 2026.74.
 */
export function magneticField(latDeg: number, lonDeg: number, altKm: number, decimalYear: number): MagneticField {
  const c = model();
  const S = (schmidtCache ??= schmidt(c.nMax));
  const dt = decimalYear - c.epoch;

  // Geodetic → geocentric spherical.
  const phi = latDeg * D2R;
  const lam = lonDeg * D2R;
  const sinPhi = Math.sin(phi);
  const cosPhi = Math.cos(phi);
  const rc = A_KM / Math.sqrt(1 - E2 * sinPhi * sinPhi);
  const p = (rc + altKm) * cosPhi;
  const zc = (rc * (1 - E2) + altKm) * sinPhi;
  const r = Math.hypot(p, zc);
  const phiC = Math.asin(zc / r); // geocentric latitude

  // Legendre functions of cos(colatitude) = sin(phiC); derivatives w.r.t. colatitude θ.
  const x = Math.sin(phiC); // cos θ
  const z = Math.cos(phiC); // sin θ
  const N = c.nMax;
  const P = square(N);
  const dP = square(N);
  P[0]![0] = 1;
  dP[0]![0] = 0;
  for (let n = 1; n <= N; n++) {
    for (let m = 0; m <= n; m++) {
      if (n === m) {
        P[n]![n] = z * P[n - 1]![n - 1]!;
        dP[n]![n] = z * dP[n - 1]![n - 1]! + x * P[n - 1]![n - 1]!;
      } else if (n === 1 && m === 0) {
        P[1]![0] = x * P[0]![0]!;
        dP[1]![0] = x * dP[0]![0]! - z * P[0]![0]!;
      } else {
        const k = n > 1 && m <= n - 2 ? ((n - 1) ** 2 - m * m) / ((2 * n - 1) * (2 * n - 3)) : 0;
        const p2 = n > 1 && m <= n - 2 ? P[n - 2]![m]! : 0;
        const dp2 = n > 1 && m <= n - 2 ? dP[n - 2]![m]! : 0;
        P[n]![m] = x * P[n - 1]![m]! - k * p2;
        dP[n]![m] = x * dP[n - 1]![m]! - z * P[n - 1]![m]! - k * dp2;
      }
    }
  }

  let bx = 0; // north (spherical)
  let by = 0; // east
  let bz = 0; // down
  let ar = (RE_KM / r) ** 2;
  for (let n = 1; n <= N; n++) {
    ar *= RE_KM / r; // (a/r)^(n+2)
    for (let m = 0; m <= n; m++) {
      const gnm = c.g[n]![m]! + dt * c.gd[n]![m]!;
      const hnm = c.h[n]![m]! + dt * c.hd[n]![m]!;
      const cosM = Math.cos(m * lam);
      const sinM = Math.sin(m * lam);
      const pnm = S[n]![m]! * P[n]![m]!;
      const dpnm = S[n]![m]! * dP[n]![m]!;
      const t = gnm * cosM + hnm * sinM;
      bx += ar * t * dpnm;
      bz -= (n + 1) * ar * t * pnm;
      by += ar * m * (gnm * sinM - hnm * cosM) * pnm;
    }
  }
  // The east component divides by sin θ; at the poles use the limit (WMM "MAG_SummationSpecial").
  if (Math.abs(z) > 1e-10) by /= z;
  else by = polarEast(c, dt, x, lam, r);

  // Rotate spherical (geocentric) → geodetic frame.
  const psi = phiC - phi;
  const X = bx * Math.cos(psi) - bz * Math.sin(psi);
  const Y = by;
  const Z = bx * Math.sin(psi) + bz * Math.cos(psi);
  const H = Math.hypot(X, Y);
  const Fv = Math.hypot(H, Z);
  const decl = Math.atan2(Y, X) * R2D;
  let gv: number | null = null;
  if (Math.abs(latDeg) > 55) gv = latDeg > 0 ? decl - lonDeg : decl + lonDeg;
  if (gv !== null) gv = ((gv + 540) % 360) - 180;
  return {
    x: X,
    y: Y,
    z: Z,
    h: H,
    f: Fv,
    inclinationDeg: Math.atan2(Z, H) * R2D,
    declinationDeg: decl,
    gridVariationDeg: gv,
    decimalYear,
    outsideValidity: decimalYear < c.epoch || decimalYear > WMM_VALID_UNTIL,
  };
}

/** East component at the geographic poles (sin θ = 0): recursion for m = 1 terms only. */
function polarEast(c: Coefficients, dt: number, cosTheta: number, lam: number, r: number): number {
  const N = c.nMax;
  const PS = new Array<number>(N + 1).fill(0);
  PS[0] = 1;
  PS[1] = 1;
  let by = 0;
  let ar = (RE_KM / r) ** 2;
  const S = (schmidtCache ??= schmidt(N));
  for (let n = 1; n <= N; n++) {
    ar *= RE_KM / r;
    if (n > 1) {
      const k = ((n - 1) ** 2 - 1) / ((2 * n - 1) * (2 * n - 3));
      PS[n] = cosTheta * PS[n - 1]! - k * PS[n - 2]!;
    }
    const gnm = c.g[n]![1]! + dt * c.gd[n]![1]!;
    const hnm = c.h[n]![1]! + dt * c.hd[n]![1]!;
    by += ar * (gnm * Math.sin(lam) - hnm * Math.cos(lam)) * PS[n]! * S[n]![1]!;
  }
  return by;
}

/** Magnetic declination (degrees, positive EAST). */
export function magneticDeclination(latDeg: number, lonDeg: number, altKm: number, decimalYear: number): number {
  return magneticField(latDeg, lonDeg, altKm, decimalYear).declinationDeg;
}

/** Epoch ms → decimal year (e.g. 2026-09-29 → 2026.74), leap-year aware. */
export function decimalYear(t: EpochMs): number {
  const d = new Date(t);
  const y = d.getUTCFullYear();
  const start = Date.UTC(y, 0, 1);
  const end = Date.UTC(y + 1, 0, 1);
  return y + (t - start) / (end - start);
}

/** Declination at an instant (sea level unless `altKm`). */
export function declinationAt(lat: number, lon: number, t: EpochMs, altKm = 0): number {
  return magneticDeclination(lat, lon, altKm, decimalYear(t));
}

/** °T → °M: magnetic = true − declination (east declination is positive). */
export function trueToMagnetic(trueDeg: number, declinationDeg: number): number {
  return normalizeDeg(trueDeg - declinationDeg);
}

/** °M → °T: true = magnetic + declination. Use for iOS/web compass headings (CLHeading.magneticHeading, deviceorientation alpha). */
export function magneticToTrue(magneticDeg: number, declinationDeg: number): number {
  return normalizeDeg(magneticDeg + declinationDeg);
}

/** "12.6° E" / "3.1° W". */
export function formatDeclination(declinationDeg: number): string {
  const v = Math.abs(declinationDeg).toFixed(1);
  if (Number(v) === 0) return '0.0°';
  return `${v}° ${declinationDeg > 0 ? 'E' : 'W'}`;
}
